/**
 * `/app/{tenant}/play-entry/help` — the Play Entry field reference.
 *
 * The laptop layout of Play Entry renders NO inline tooltips: a help button
 * beside each of ~25 labels is noise a booth operator never needs between
 * snaps. The same registry text (`src/registry/tooltips.json`, with any
 * tenant / platform overrides applied) lives here instead, one screen grouped
 * the way the form is, so an operator reads it once before kickoff and a
 * coordinator can point a new operator at a single URL. Tablet and phone keep
 * their tap-on-focus tooltips and link here from the screen switcher.
 *
 * The longer how-to (offline queue, conflicts, autofill) stays in the docs
 * site at `/app/docs/play-entry`; this page links to it rather than repeating
 * it.
 */
import { Link } from 'react-router-dom';

import { useSession } from '../../app/session-context';
import { resolveTooltip } from '../../components/tooltip';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { APP_VIEWS } from '../../lib/constants';

import { PHASE_LABELS, type PlayPhase } from './play-catalog';

interface HelpGroup {
  id: string;
  title: string;
  /** Which phases the group appears in; omitted = every phase. */
  phases?: readonly PlayPhase[];
  tooltipIds: readonly string[];
}

/** Mirrors the form's row order so the reference reads top-to-bottom like the page. */
export const PLAY_ENTRY_HELP_GROUPS: readonly HelpGroup[] = [
  {
    id: 'toolbar',
    title: 'Phase and play type',
    tooltipIds: ['play_entry.o_d_st_toggle', 'play_entry.play_type'],
  },
  {
    id: 'situation',
    title: 'Situation',
    tooltipIds: [
      'play_entry.quarter',
      'play_entry.game_clock',
      'play_entry.down',
      'play_entry.los',
      'play_entry.first_down_line',
      'play_entry.hash',
    ],
  },
  {
    id: 'offense',
    title: 'Offense tactical details',
    phases: ['offense'],
    tooltipIds: [
      'play_entry.offensive_formation',
      'play_entry.offensive_personnel',
      'play_entry.motion',
      'play_entry.protection',
      'play_entry.opponent_coverage',
      'play_entry.opponent_front',
      'play_entry.opponent_blitz',
    ],
  },
  {
    id: 'defense',
    title: 'Defense tactical details',
    phases: ['defense'],
    tooltipIds: [
      'play_entry.opponent_formation',
      'play_entry.defensive_front',
      'play_entry.defensive_coverage',
      'play_entry.defensive_blitz',
    ],
  },
  {
    id: 'special-teams',
    title: 'Special Teams details',
    phases: ['special_teams'],
    tooltipIds: ['play_entry.kick_distance', 'play_entry.return_yards'],
  },
  {
    id: 'result',
    title: 'Result',
    tooltipIds: [
      'play_entry.play_outcome',
      'play_entry.specialty_outcome',
      'play_entry.fumble',
      'play_entry.fumble_recovered',
      'play_entry.gain_loss',
      'play_entry.success_measure',
    ],
  },
  {
    id: 'notes',
    title: 'Playbook, notes and tags',
    tooltipIds: [
      'play_entry.playbook_number',
      'play_entry.play_name',
      'play_entry.notes',
      'play_entry.tags',
      'play_entry.red_zone',
      'play_entry.scramble',
      'play_entry.offensive_penalty',
      'play_entry.defensive_penalty',
    ],
  },
  {
    id: 'sync',
    title: 'Logging, clearing and syncing',
    tooltipIds: ['play_entry.clear_all', 'play_entry.offline_queue'],
  },
];

export function PlayEntryHelpPage(): JSX.Element {
  useDocumentTitle('Play Entry help');
  const { tenantSlug } = useSession();
  const tenantPath = `/app/${tenantSlug ?? ''}`;

  return (
    <section className="view play-entry-help">
      <header className="view__header">
        <div>
          <h1>Play Entry help</h1>
          <p className="muted">
            What every field on the Play Entry form means, grouped the way the form is laid out.
            Read it once before kickoff — the laptop layout keeps the form itself free of help
            buttons so nothing slows you down between snaps.
          </p>
        </div>
        <nav className="play-entry-help__actions" aria-label="Play Entry help links">
          <Link className="btn btn--primary" to={`${tenantPath}/${APP_VIEWS.playEntry}`}>
            Back to Play Entry
          </Link>
          <Link className="btn btn--ghost" to="/app/docs/play-entry">
            Full guide
          </Link>
        </nav>
      </header>

      <nav className="play-entry-help__toc" aria-label="Sections">
        {PLAY_ENTRY_HELP_GROUPS.map((group) => (
          <a key={group.id} href={`#help-${group.id}`}>
            {group.title}
          </a>
        ))}
      </nav>

      <div className="play-entry-help__groups">
        {PLAY_ENTRY_HELP_GROUPS.map((group) => {
          const entries = group.tooltipIds
            .map((tooltipId) => ({ tooltipId, entry: resolveTooltip(tooltipId) }))
            .filter((item) => item.entry !== null);
          if (entries.length === 0) return null;
          return (
            <section
              key={group.id}
              className="play-entry-help__group"
              id={`help-${group.id}`}
              aria-labelledby={`help-${group.id}-title`}
            >
              <h2 id={`help-${group.id}-title`}>
                {group.title}
                {group.phases === undefined ? null : (
                  <span className="play-entry-help__phase">
                    {group.phases.map((phase) => PHASE_LABELS[phase]).join(', ')} only
                  </span>
                )}
              </h2>
              <dl className="play-entry-help__list">
                {entries.map(({ tooltipId, entry }) => (
                  <div key={tooltipId} className="play-entry-help__item">
                    <dt>{entry?.label}</dt>
                    <dd>{entry?.text}</dd>
                  </div>
                ))}
              </dl>
            </section>
          );
        })}
      </div>

      <p className="muted play-entry-help__footer">
        Layouts: on a laptop everything sits on one screen with the phase and play type as toggle
        buttons. On a tablet the selection cards lead; swipe in from the left edge for the menu and
        up from the bottom edge to switch screens. On a phone the form is four steps — swipe
        sideways between them and slide your finger across the yardage strip to set gain / loss.
      </p>
    </section>
  );
}
